import React, { useState, useEffect, useMemo } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Button, Typography, Box, Tooltip, styled, Select, MenuItem, Checkbox, FormControlLabel, IconButton, Popover, Divider } from "@mui/material";
import SettingsIcon from '@mui/icons-material/Settings';
import StudentLogs from "./StudentLogs";
import { useParams, useNavigate } from "react-router-dom";
import PulseLoader from '@/components/PulseLoader';
import { Download } from "lucide-react";
import MediaQuery from 'react-responsive';
import { fetchSessionInfo } from '../../../liveSession/api';

// Type definitions
interface LogEntry {
  session_start_time: string;
  session_end_time: string | null;
}

interface Participant {
  student_id: string;
  display_name: string;
  collegeName: string;
  branch: string;
  email: string;
  phone_number: string;
  present: boolean;
  attendedTime: number;
  attendance_percentage?: number;
  log?: LogEntry[];
  course_name?: string;
  batch_name?: string;
}

interface SessionInfo {
  session_duration: number;
}

interface ApiResponse {
  participantInfo: Participant[];
  sessionInfo: SessionInfo;
}

const SessionParticipants: React.FC = () => {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [sessionDuration, setSessionDuration] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { sessionName } = useParams<{ sessionName: string }>();
  const navigate = useNavigate(); // Navigation hook
  
  // Get session ID from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('sessionId');
  
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
    student_id: false,
    display_name: true,
    collegeName: true,
    branch: true,
    course_name: true,
    batch_name: true,
    email: false,
    phone_number: true,
    present: true,
    attendance_percentage: true,
  };
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({ ...DEFAULT_COLUMN_VISIBILITY });
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);
  const isSettingsOpen = Boolean(settingsAnchorEl);
  const handleOpenSettings = (event: React.MouseEvent<HTMLElement>) => setSettingsAnchorEl(event.currentTarget);
  const handleCloseSettings = () => setSettingsAnchorEl(null);
  const handleResetVisibility = () => setColumnVisibility({ ...DEFAULT_COLUMN_VISIBILITY });

  const handleSelectAllChange = (): void => {
    if (selectAll) {
      setSelectedRows([]); // Deselect all if already selected
    } else {
      setSelectedRows(participants.map(participant => participant.student_id)); // Select all students
    }
    setSelectAll(!selectAll);
  };

  const handleSelectRowChange = (student_id: string): void => {
    const newSelectedRows = selectedRows.includes(student_id)
      ? selectedRows.filter(id => id !== student_id)
      : [...selectedRows, student_id];
    setSelectedRows(newSelectedRows);
    setSelectAll(newSelectedRows.length === participants.length); // Update select all checkbox
  };

  const CustomTooltip = styled(({ className, ...props }: any) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    "& .MuiTooltip-tooltip": {
      backgroundColor: "white",
      color: "black",
      boxShadow: theme.shadows[1],
      fontSize: 13,
      border: "1px solid #dadde9",
      "& .MuiTooltip-arrow": {
        color: "white",
      },
    },
  }));

  useEffect(() => {
    // Check if user is trying to access admin route without admin permission
    const currentPath = window.location.pathname;
    const isAdminRoute = currentPath.startsWith('/admin');
    const hasAdminPermission = sessionStorage.getItem("Admin");
    
    if (isAdminRoute && !hasAdminPermission) {
      // Redirect to trainer equivalent route
      const trainerPath = currentPath.replace('/admin', '/trainer');
      navigate(trainerPath);
      return;
    }
  }, [navigate]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      // Check if sessionId exists before making the API call
      if (!sessionId) {
        console.error("Session ID is missing from URL parameters");
        setError("Session ID is missing from URL");
        setIsLoading(false);
        return;
      }
      try {
        const data: ApiResponse = await fetchSessionInfo(sessionId);
        setParticipants(data.participantInfo || []);
        setSessionDuration(data.sessionInfo?.session_duration || 1);
      } catch (err) {
        setError("Failed to fetch data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [sessionId]);

  const filteredParticipants = useMemo(() => {
    return participants.filter(
      (participant) => participant.student_id !== "0000"
    );
  }, [participants]);

  const attendanceSummary = useMemo(() => {
    const attendedCount = filteredParticipants.filter((p) => p.present).length;
    const totalInvited = filteredParticipants.length;
    return `${attendedCount}/${totalInvited} attended`;
  }, [filteredParticipants]);

  const columns = useMemo(() => {
    return [
      {
        accessorKey: "student_id",
        header: "ID",
        size: 50,
        filterFn: "contains" as const,
      },
      {
        accessorKey: "display_name",
        header: "Student Name",
        filterFn: "contains" as const,
      },
      {
        accessorKey: "collegeName",
        header: "College",
        filterFn: "contains" as const,
        size: 50,
      },
      {
        accessorKey: "branch",
        header: "Branch",
        filterFn: "contains" as const,
        size: 50
      },
      {
        accessorKey: "course_name",
        header: "Course",
        filterFn: "contains" as const,
        size: 50
      },
      {
        accessorKey: "batch_name",
        header: "Batch",
        filterFn: "contains" as const,
        size: 50
      },
      {
        accessorKey: "email",
        header: "Email",
        filterFn: "contains" as const,
      },
      {
        accessorKey: "phone_number",
        header: "Phone",
        filterFn: "contains" as const,
        size: 50
      },
      {
        accessorKey: "present",
        header: "Status",
        filterVariant: 'select' as const,
        filterSelectOptions: ['All', 'Present', 'Absent'],
        filterFn: (row: any, _columnIds: any, filterValue: string[]) => {
          if (!filterValue.length || filterValue.includes('All')) return true;
          const isPresent = row.original.present;
          return filterValue.includes(isPresent ? 'Present' : 'Absent');
        },
        Cell: ({ cell }: { cell: { getValue: () => boolean } }) => (
          <Typography
            sx={{
              color: cell.getValue() ? "green" : "red",
              fontWeight: cell.getValue() ? "normal" : "bold",
            }}
          >
            {cell.getValue() ? "Present" : "Absent"}
          </Typography>
        ),
      },
      {
        accessorKey: "attendance_percentage",
        header: "Attendance",
        filterFn: "greaterThan" as const,
        Cell: ({ row }: { row: { original: Participant } }) => {
          const attendedTime = row.original.attendedTime || 0;
          const percentage =
            sessionDuration > 0
              ? Math.round((attendedTime / sessionDuration) * 100)
              : 0;
          const logCount = row.original.log?.length || 0;

          return (
            <CustomTooltip
              title={<StudentLogs participant={row.original} />}
              arrow
              placement="top"
            >
              <div
                style={{
                  color: percentage < 50 ? "red" : "green",
                  fontWeight: percentage < 50 ? "bold" : "normal",
                  cursor: "pointer",
                }}
              >
                {percentage}% ({logCount})
              </div>
            </CustomTooltip>
          );
        },
      },
    ];
  }, [sessionDuration]);

  const table = useMaterialReactTable({
    columns,
    muiTablePaperProps: {
      sx: {
        borderRadius: "10px",
      },
    },
    enableColumnFilters: true,
    positionToolbarAlertBanner: "bottom" as const,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 15,
      },
      showColumnFilters: false,
      density: "compact" as const,
      columnVisibility: { ...DEFAULT_COLUMN_VISIBILITY },
    },
    muiTopToolbarProps: {
      sx: { borderBottom: '2px solid rgba(0,0,0,0.2)' },
    },
    data: filteredParticipants,
    enableRowSelection: true,
    enableHiding: true,
    enablePagination: true,
    onRowSelectionChange: (updaterOrValue) => {
      const newSelection = typeof updaterOrValue === 'function' 
        ? updaterOrValue(selectedRows.reduce((acc, id) => ({ ...acc, [id]: true }), {}))
        : updaterOrValue;
      
      const selectedIds = Object.keys(newSelection).filter(id => newSelection[id]);
      setSelectedRows(selectedIds);
      setSelectAll(selectedIds.length === filteredParticipants.length && filteredParticipants.length > 0);
    },
    state: {
      rowSelection: selectedRows.reduce((acc, id) => ({ ...acc, [id]: true }), {}),
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getRowId: (row: Participant) => row.student_id,
    muiTableContainerProps: {
      sx: {
        height: "calc(100vh - 228px)",
        overflow: 'auto'
      }
    },
    muiPaginationProps: {
      rowsPerPageOptions: [15, 20, 25, 30, 50, 100, 200],
      showFirstButton: true,
      showLastButton: true,
    },
    renderTopToolbarCustomActions: ({ table }) => {
      const selectedRowsCount = table.getSelectedRowModel().rows.length;
      return (
        <Box
          sx={{
            display: "flex",
            gap: "0.5rem",
            p: "0.5rem",
            alignItems: "center",
            width: '100%',
            position: 'relative'
          }}
        >
          <Button
            color="primary"
            disabled={selectedRowsCount === 0}
            onClick={handleExport}
            variant="contained"
            startIcon={<Download size={20} />}
            size="small"
          >
            Export ({selectedRowsCount})
          </Button>
          <Box sx={{ position: 'absolute', right: 0, top: 6 }}>
            <IconButton size="small" onClick={handleOpenSettings} title="Column Visibility">
              <SettingsIcon />
            </IconButton>
          </Box>
            <Popover
              open={isSettingsOpen}
              anchorEl={settingsAnchorEl}
              onClose={handleCloseSettings}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ p: 2, width: 280 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Column Visibility</Typography>
                  <Button size="small" onClick={handleResetVisibility}>Reset</Button>
                </Box>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.display_name} onChange={(e) => setColumnVisibility((p) => ({ ...p, display_name: e.target.checked }))} />} label="Student Name" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.student_id} onChange={(e) => setColumnVisibility((p) => ({ ...p, student_id: e.target.checked }))} />} label="Student ID" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.collegeName} onChange={(e) => setColumnVisibility((p) => ({ ...p, collegeName: e.target.checked }))} />} label="College" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.branch} onChange={(e) => setColumnVisibility((p) => ({ ...p, branch: e.target.checked }))} />} label="Branch" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.course_name} onChange={(e) => setColumnVisibility((p) => ({ ...p, course_name: e.target.checked }))} />} label="Course" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.batch_name} onChange={(e) => setColumnVisibility((p) => ({ ...p, batch_name: e.target.checked }))} />} label="Batch" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.phone_number} onChange={(e) => setColumnVisibility((p) => ({ ...p, phone_number: e.target.checked }))} />} label="Phone" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.email} onChange={(e) => setColumnVisibility((p) => ({ ...p, email: e.target.checked }))} />} label="Email" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.present} onChange={(e) => setColumnVisibility((p) => ({ ...p, present: e.target.checked }))} />} label="Status" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!columnVisibility.attendance_percentage} onChange={(e) => setColumnVisibility((p) => ({ ...p, attendance_percentage: e.target.checked }))} />} label="Attendance %" />
                </Box>
              </Box>
            </Popover>
        </Box>
      );
    },
  });

  const handleExport = (): void => {
    // Get data to export - use selected participants from filteredParticipants to match table display
    const dataToExport = selectedRows.length
      ? filteredParticipants.filter((participant) => selectedRows.includes(participant.student_id))
      : filteredParticipants;

    if (dataToExport.length === 0) {
      alert("No rows selected to export.");
      return;
    }

    // Create CSV content with attendance in same format as displayed
    const csvContent = [
      [
        "Student ID",
        "Student Name",
        "Email",
        "College",
        "Branch",
        "Course",
        "Batch",
        "Phone",
        "Status",
        "Attendance"
      ],
      ...dataToExport.map((row) => {
        const attendedTime = row.attendedTime || 0;
        const percentage = sessionDuration > 0
          ? Math.round((attendedTime / sessionDuration) * 100)
          : 0;
        const logCount = row.log?.length || 0;
        const attendance = `${percentage}% (${logCount})`;
        
        return [
          row.student_id,
          row.display_name,
          row.email,
          row.collegeName || '',
          row.branch || '',
          row.course_name || '',
          row.batch_name || '',
          row.phone_number || '',
          row.present ? 'Present' : 'Absent',
          attendance
        ];
      }),
    ]
      .map((row) =>
        row
          .map((cell) =>
            // Properly escape cells that contain commas or quotes
            typeof cell === "string"
              ? `"${cell.replace(/"/g, '""')}"`
              : `"${cell}"`
          )
          .join(",")
      )
      .join("\n");

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    link.setAttribute("href", url);
    link.setAttribute("download", `participants_${sessionId}_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="my-2 me-2 rounded-2 bg-white" style={{ height: `calc(100vh - 90px)`,display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <PulseLoader color="#1976d2" />
      </div>
    );
  }
  
  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <div className="my-2 me-2 rounded-2" style={{ backgroundColor: "white", height: `calc(100vh - 95px)`,overflowY: "auto" }}>
      <MediaQuery minWidth={1137}>
      <Box>
      <MaterialReactTable table={table} />
    </Box>
    </MediaQuery>
    <MediaQuery maxWidth={1136}>
 <Box sx={{ p: 1, backgroundColor: '#fff', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)', borderRadius: 1 }}>
   <div style={{height:'35px',maxHeight:'35px'}}>
   <div className="d-flex justify-content-between ">
     <h5>Report</h5>
     <Button
            className="btn btn-sm btn-primary"
            color="primary"
            disabled={selectedRows.length === 0}
            onClick={handleExport}
            variant="contained"
            startIcon={<Download size={20} />}
            size="small"
          >
            Export
          </Button>
   </div>

   </div>
   
   <div style={{ height: 'calc(100vh - 150px)', overflow: 'auto' }}>
     <table style={{ width: '100%', borderCollapse: 'collapse' }}>
       <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f1f1f1', zIndex: 10 }}>
         <tr>
           <th>
             <input
               type="checkbox"
               checked={selectAll}
               onChange={handleSelectAllChange}
             />
           </th>
           <th>ID</th>
           <th>Name</th>
           <th>College-Branch</th>
           <th>Course-Batch</th>
           <th>Phone</th>
           <th>Email</th>
           <th>Status</th>
           <th>%</th>
         </tr>
       </thead>
       <tbody>
         {participants.map((participant) => (
           <tr key={participant.student_id}>
             <td>
               <input
                 type="checkbox"
                 checked={selectedRows.includes(participant.student_id)}
                 onChange={() => handleSelectRowChange(participant.student_id)}
               />
             </td>
             <td>{participant.student_id}</td>
             <td>{participant.display_name}</td>
             <td>{participant.collegeName}-{participant.branch}</td>
             <td>{participant.course_name || ''}-{participant.batch_name || ''}</td>
             <td>{participant.phone_number}</td>
             <td>{participant.email}</td>
             <td style={{ color: participant.present ? 'green' : 'red' }}>
               {participant.present ? 'Present' : 'Absent'}
             </td>
             <td>
               <CustomTooltip
                 title={<StudentLogs participant={participant} />}
                 arrow
                 placement="top"
               >
                 <span style={{ cursor: 'pointer' }}>
                   {sessionDuration > 0 
                     ? `${Math.round((participant.attendedTime / sessionDuration) * 100)}% (${participant.log?.length || 0})`
                     : `0% (${participant.log?.length || 0})`}
                 </span>
               </CustomTooltip>
             </td>
           </tr>
         ))}
       </tbody>
     </table>
   </div>
 </Box>
</MediaQuery>
    </div>
  );
};

export default SessionParticipants;
